// The prompt chain. A sighted-visitor convenience only: every prompt is
// aria-hidden and everything it teaches is also in the canvas's described-visual
// text (AccessibleEntry), so nothing here is load-bearing.
//
//   load      -> DRAG TO ORBIT            (the only thing said unprompted)
//   orbit     -> CLICK A NODE             (after the first orbit drag)
//   open      -> what the panel offers    (after the first node is opened)
//   hover     -> +N OPENS IT UP           (first hover of a node showing +n)
//   nodeDrag  -> silence                  (chain complete)
//
// Every prompt is independent: it fires when ITS action happens and it has not
// been shown before, whatever order the visitor explores in. Ordering guarantees:
// the load prompt is first (nothing else fires until it has been shown), and
// no prompt fires while another is on screen. A prompt that would collide is
// DROPPED, not queued, and not marked shown: a queued prompt would surface
// seconds late, away from the action that earned it; dropping lets the same
// action teach it the next time it happens. nodeDrag has no prompt of its own
// (the described visual covers it); it is simply recorded as handled.
// localStorage throws in private mode and with blocked site data, so every
// access is guarded; progress then lives in memory for the visit.
export const KEYS = {
  load: "plate.hint.load", orbit: "plate.hint.orbit",
  open: "plate.hint.open", hover: "plate.hint.hover", drag: "plate.hint.drag",
};
// Note: an earlier build wrote plate.hint.done as a terminal flag. It is never
// read, so a visitor carrying it is not stuck in a finished state.

export const PROMPTS = {
  load: "DRAG TO ORBIT",
  orbit: "CLICK A NODE",
  openLive: "FOLLOW THE LINK TO GO DEEPER",
  openNoLive: "LINKS TO PROJECTS APPEAR HERE",
  hover: (n) => `+${n} OPENS IT UP`,
};

// THE OPENING PROMPT IS REMEMBERED FOR THE SESSION, NOT FOREVER.
// "DRAG TO ORBIT" is the one instruction given unprompted, and it is the only
// thing standing between a visitor and a drawing that looks like a picture. A
// permanent flag meant anyone who had loaded the page once - Liz above all,
// who reloads it constantly - never saw it again and the map arrived with no
// way in. Per session: a new visit is told, a reload within the visit is not
// nagged. The other three stay once-ever in localStorage; those are
// discoveries, and repeating them would undo the game-like feel she asked for.
const SESSION_KEYS = new Set([KEYS.load]);

function store(key) {
  return SESSION_KEYS.has(key) ? window.sessionStorage : window.localStorage;
}

export function readFlag(key) {
  try {
    return store(key).getItem(key) === "1";
  } catch (e) {
    return false;
  }
}

export function writeFlag(key) {
  try {
    store(key).setItem(key, "1");
  } catch (e) {
    /* storage unavailable: the chain simply may replay next visit */
  }
}

export function loadState() {
  const shown = {};
  Object.keys(KEYS).forEach((k) => { shown[k] = readFlag(KEYS[k]); });
  return { shown };
}

// Finished is derived: every step has been shown (or, for drag, done).
export function isFinished(state) {
  return Object.keys(KEYS).every((k) => state.shown[k]);
}

function mark(state, step) {
  writeFlag(KEYS[step]);
  return { ...state, shown: { ...state.shown, [step]: true } };
}

// Pure but for the guarded write: (state, event, {busy}) -> { state, prompt }.
// prompt is null when nothing is said.
export function advance(state, event, { busy = false } = {}) {
  const none = { state, prompt: null };
  const { shown } = state;
  const say = (step, text) =>
    shown[step] || busy ? none : { state: mark(state, step), prompt: { id: step, text } };
  if (event.type === "load") return say("load", PROMPTS.load);
  if (event.type === "nodeDrag") return shown.drag ? none : { state: mark(state, "drag"), prompt: null };
  if (!shown.load) return none;
  switch (event.type) {
    case "orbit":
      return say("orbit", PROMPTS.orbit);
    case "open":
      return say("open", event.live ? PROMPTS.openLive : PROMPTS.openNoLive);
    case "hover":
      return event.count > 0 ? say("hover", PROMPTS.hover(event.count)) : none;
    default:
      return none;
  }
}
